import { makeObservable, observable, action, computed } from "mobx";


import { ToastHelper } from "helpers/ToastHelper";


import StorageUtil, { KEYS } from "helpers/utils/StorageUtil";
import { UserModel } from "models/user/shared/UserModel";
import { SeparatorModel } from "models/user/separator/entities/SeparatorModel";
import UserAPI from "services/UserAPI";
import { CustomerModel } from "models/user/customer/entities/CustomerModel";

/**
 * Store para gerenciar o estado relacionado a usuários (clientes) e separadores.
 */
class UserStore {
    // --- Estado Observável ---
    customer?: CustomerModel;
    separator?: SeparatorModel;
    loading: boolean = false;


    constructor() {

        makeObservable(this, {
            // Observables
            customer: observable,
            separator: observable,
            loading: observable,

            // Computed
            separatorUuid: computed,

            // actions
            reset: action,
            emptyUser: action,
            getCustomer: action,
            setSeparatorKeys: action,
            clearSeparatorKeys: action,
            getSeparator: action,
        });
    }

    /**
     * Reseta o estado da store e limpa dados de separador do storage.
     */
    reset(): void {
        this.customer = undefined;
        this.loading = false;
        this.clearSeparatorKeys();
    }


    /**
     * Cria uma instância de um novo usuário vazio.
     */
    emptyUser(): void {
        this.customer = undefined;
    }

    /**
     * Busca os dados de um cliente (customer).
     * @param uuid Opcional. O UUID do cliente. Se não for fornecido, busca do storage.
     * @returns O modelo do cliente ou undefined em caso de erro.
     */
    async getCustomer(userUuid: string): Promise<CustomerModel | undefined> {
        this.loading = true;


        if (!userUuid) {
            this.loading = false;
            ToastHelper.error({ message: "UUID do usuário não encontrado." });
            return undefined;
        }

        try {
            const response = await UserAPI.getCustomer(userUuid);
            if (!response.error) {
                this.customer = new CustomerModel(response);
                return this.customer;
            } else {
                ToastHelper.error({ message: response.error });
            }
        } catch (error) {
            ToastHelper.error({ message: "Erro ao buscar dados do cliente." });
            console.error("Falha em getCustomer:", error);
        } finally {
            this.loading = false;
        }
        return undefined;
    }

    get separatorUuid(): string | null {
        return StorageUtil.getItem(KEYS.SEPARATOR_UUID);
    }

    /**
     * Salva as credenciais do separador no storage.
     */
    setSeparatorKeys(uuid: string, token: string): void {
        StorageUtil.setItem(KEYS.SEPARATOR_UUID, uuid);
        StorageUtil.setItem(KEYS.SEPARATOR_TOKEN, token);
    }

    /**
     * Remove as credenciais do separador do storage.
     */
    clearSeparatorKeys(): void {
        StorageUtil.remove(KEYS.SEPARATOR_UUID);
        StorageUtil.remove(KEYS.SEPARATOR_TOKEN);
    }

    /**
     * Busca os dados de um separador.
     * @param uuid Opcional. O UUID do separador. Se não for fornecido, busca do storage.
     * @returns O modelo do separador ou undefined em caso de erro.
     */
    async getSeparator(uuid?: string): Promise<SeparatorModel | undefined> {
        this.loading = true;
        const separatorUuid = uuid || StorageUtil.getItem(KEYS.SEPARATOR_UUID);

        if (!separatorUuid) {
            this.loading = false;
            // Não mostra erro aqui, pois pode ser um fluxo normal não ter separador
            return undefined;
        }

        try {
            const response = await UserAPI.getSeparator(separatorUuid);
            if (!response.error) {
                this.separator = new SeparatorModel(response);
                return this.separator;
            } else {
                ToastHelper.error({ message: response.error });
                this.clearSeparatorKeys();
            }
        } catch (error) {
            ToastHelper.error({ message: "Erro ao configurar dispositivo." });
            this.clearSeparatorKeys();
            console.error("Falha em getSeparator:", error);
        } finally {
            this.loading = false;
        }
        return undefined;
    }
}

export default UserStore;